import type { ImgHTMLAttributes } from 'react';
import { memo, useEffect, useRef } from 'react';
import { deepEqual } from 'fast-equals';

import { cn } from 'helpers/classnames';

import css from 'styles/components/common/image.scss';

const defaultProps = {
  placeholder: <div className={cn(css.lazyLoadSpinner)} />
};

// remove placeholder global html attribute from the type so its not mistaken as the placeholder for the LazyImage
export type ImageWithoutPlaceholderProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'placeholder'> & {
  fetchPriority?: 'high' | 'low' | 'auto';
};

export type WebpImageProps = { webp: { src: string; srcSet: string } };
type PicturePropsType = {
  pictureProps?: JSX.Element[] | null;
  hasAspect?: boolean;
  isFullWidth?: boolean;
};
export type ImageProps = ImageWithoutPlaceholderProps & Partial<typeof defaultProps> & Partial<WebpImageProps> & PicturePropsType;

// you can use this if your component is memoized already
export const NonMemoImage = ({
  id,
  className,
  isFullWidth,
  hasAspect,
  pictureProps = null,
  loading = 'lazy',
  alt,
  fetchPriority,
  sizes,
  ...rest
}: ImageProps) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const placeholderRef = useRef<HTMLDivElement>(null);

  // would prefer useLayoutEffect, but it causes hydration issues :(
  useEffect(() => {
    const img = imgRef.current;
    if (!img) {
      return;
    }
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame?.(() => {
        if (!img?.complete) {
          if (placeholderRef.current) {
            placeholderRef.current.style.display = 'block';
          }
          img.style.opacity = '0.2';
          // install the handler to turn off the spinner when finished.
          const { onload } = img;
          img.onload = ev => {
            // image finished loading turn off the
            img.style.opacity = '1';
            if (placeholderRef.current) {
              placeholderRef.current.style.display = 'none';
            }
            onload && onload.call(img, ev);
          };
        } else {
          // image already loaded  ensure the image is shown
          // and that the spinner is off.
          img!.style.opacity = '1';
          if (placeholderRef.current) {
            placeholderRef.current.style.display = 'none';
          }
        }
      });
    }
  }, [imgRef.current, placeholderRef.current]);

  const { webp: { srcSet: webpSrcSet } = {}, ...restProps } = rest;

  return (
    <div id={id} className={cn(className, css.image, { [css.fullWidth]: isFullWidth, [css.hasStyledAspectRatio]: hasAspect })}>
      <div className={cn(css.lazyLoadSpinner)} ref={placeholderRef} />
      <picture className={cn(css.lazyLoadPicture)}>
        {webpSrcSet && <source srcSet={webpSrcSet} type="image/webp" sizes={sizes} />}
        {pictureProps}
        <img
          ref={imgRef}
          loading={loading}
          className={cn(css.lazyLoadImage)}
          alt={alt}
          sizes={sizes}
          {...restProps}
          // @ts-ignore
          fetchpriority={fetchPriority}
        />
      </picture>
    </div>
  );
};

/**
 * Preload an image off-DOM (with webp source if available). Resolves once the
 * browser has loaded it; rejects on network failure.
 * Used by `useImageRotation` to ensure the next angle is ready before display.
 */
export const loadImage = (img: ImageProps): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    if (typeof img.src !== 'string' || !img.src) {
      resolve();
      return;
    }

    const effectiveSizes = typeof img.sizes === 'string' ? img.sizes : '';
    const picture = document.createElement('picture');
    const el = document.createElement('img');

    el.onload = () => resolve();
    el.onerror = () => reject(new Error('image preload failed'));

    // Build the full <picture> structure first: the webp <source> must precede the
    // <img>, and the <img> must be inside the <picture> before its attributes are set
    // so the browser's source selection sees the complete context.
    const webpSrcSet = img.webp?.srcSet;
    if (webpSrcSet) {
      const source = document.createElement('source');
      source.type = 'image/webp';
      source.srcset = webpSrcSet;
      source.sizes = effectiveSizes;
      picture.appendChild(source);
    }
    picture.appendChild(el);

    el.sizes = effectiveSizes;
    if (typeof img.srcSet === 'string' && img.srcSet) {
      el.srcset = img.srcSet;
    }
    el.src = img.src;
  });

export default memo(NonMemoImage, deepEqual);
